import React from 'react';
import { useNavigate } from 'react-router-dom';
import PublicNavBar from './PublicNavBar';
import { Helmet } from 'react-helmet-async';

const PurchasePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-sand-100 to-sand-200">
      <Helmet>
        <title>Pricing | TeAIch Suite — AI Marking and Lesson Planning for Teachers</title>
        <meta name="description" content="One simple plan. Unlimited AI marking, lesson planning, and resource generation for teachers. First month just £0.99, then £8.99/month. Cancel anytime." />
        <link rel="canonical" href="https://teaich.app/pricing" />
      </Helmet>
      <PublicNavBar />

      <div className="py-8 sm:py-12 md:py-16 px-2 sm:px-4">
        <div className="max-w-4xl mx-auto">

          {/* Header */}
          <div className="text-center mb-8 sm:mb-12">
            <h1 className="text-5xl md:text-6xl font-bold text-coral-500 mb-4 drop-shadow-[2px_2px_0_rgba(0,0,0,1)]">
              PRICING
            </h1>
            <p className="text-lg md:text-xl text-ink-700">
              One plan. Everything included. No limits.
            </p>
          </div>

          {/* Single Pricing Card */}
          <div className="bg-white rounded-2xl border-4 border-coral-300 shadow-brutal p-8 md:p-12 text-center mb-8 max-w-2xl mx-auto relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-coral-300 text-white px-6 py-2 rounded-full font-bold text-sm">TEAICH UNLIMITED</span>
            </div>

            <div className="mt-4 mb-2">
              <span className="text-6xl font-bold text-ink-900">&pound;8.99</span>
              <span className="text-ink-500 text-lg">/month</span>
            </div>
            <p className="text-coral-600 font-semibold text-lg mb-8">First month just &pound;0.99</p>

            <div className="text-left space-y-3 mb-8">
              {[
                'Unlimited AI marking with TeAIch Assess',
                'Unlimited lesson plans with TeAIch Planner',
                'Unlimited resource generation',
                'Class insights dashboard — track student performance across all marked briefs',
                'Access to ARCHIE, our AI Assistant',
                'Detailed AI feedback on every submission',
                'Save lessons and mark schemes to your library',
              ].map((feature) => (
                <div key={feature} className="flex items-center">
                  <span className="text-coral-500 mr-3 font-bold">&#10003;</span>
                  <span className="text-ink-700">{feature}</span>
                </div>
              ))}
            </div>

            <a
              href="/login"
              className="block bg-coral-300 text-white py-4 px-8 rounded-lg font-bold text-lg hover:bg-coral-500 transition duration-200"
            >
              Get Started - First Month &pound;0.99
            </a>
            <p className="text-ink-500 text-sm mt-3">Then &pound;8.99/month - cancel anytime</p>
          </div>

          {/* Organisations */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-6 md:p-8 text-center mb-8 max-w-2xl mx-auto">
            <h3 className="text-xl font-bold text-ink-900 mb-3">FOR ORGANISATIONS</h3>
            <p className="text-ink-600 mb-4">Need multiple seats or a bespoke plan for your school or department?</p>
            <button
              onClick={() => navigate('/contact')}
              className="bg-coral-300 text-white px-8 py-3 rounded-lg font-bold hover:bg-coral-500 transition duration-200"
            >
              Enquire Now
            </button>
          </div>

          {/* Why TeAIch */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12 mb-8">
            <h3 className="text-2xl font-bold text-ink-900 text-center mb-8">WHY TEACHERS CHOOSE TeAIch</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { icon: 'T', title: 'SAVE TIME', desc: 'Mark 30 assignments in under 5 minutes instead of 3 hours' },
                { icon: 'A', title: 'AFFORDABLE', desc: '8.99 per month for unlimited access to all tools' },
                { icon: 'C', title: 'CONSISTENT', desc: 'Every student gets fair, detailed feedback every time' },
              ].map(({ icon, title, desc }) => (
                <div key={title} className="text-center">
                  <div className="w-16 h-16 bg-coral-300 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">{icon}</span>
                  </div>
                  <h4 className="text-lg font-bold text-ink-900 mb-2">{title}</h4>
                  <p className="text-ink-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* FAQ */}
          <div className="bg-white rounded-2xl border-4 border-ink-900 shadow-brutal p-8 md:p-12">
            <h3 className="text-2xl font-bold text-ink-900 text-center mb-8">FREQUENTLY ASKED QUESTIONS</h3>
            <div className="space-y-6 max-w-3xl mx-auto">
              {[
                {
                  q: 'How does the first month offer work?',
                  a: 'Your first month is just 0.99. After that it is 8.99 per month, cancel anytime.'
                },
                {
                  q: 'Can I cancel anytime?',
                  a: 'Yes - cancel at any time from your account page. You keep access until the end of your billing period.'
                },
                {
                  q: 'What file formats do you support?',
                  a: 'We support PDF files, images (JPG, PNG, GIF, BMP, WebP), and plain text.'
                },
                {
                  q: 'How accurate is the AI marking?',
                  a: 'Our AI uses advanced language models trained for educational assessment. We always recommend applying your professional judgement before sharing feedback with students.'
                },
                {
                  q: 'Is my data secure?',
                  a: 'Yes - all data is encrypted and we follow strict privacy policies. Student work is processed securely and not stored permanently.'
                },
                {
                  q: 'Do you offer school discounts?',
                  a: 'Yes! Contact us for volume pricing for schools and departments.'
                },
              ].map(({ q, a }) => (
                <div key={q}>
                  <h4 className="font-bold text-ink-900 mb-1">{q}</h4>
                  <p className="text-ink-600 text-sm">{a}</p>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>

      <footer className="bg-sand-100 border-t-4 border-coral-300 py-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div onClick={() => navigate('/')} className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/images/craidle-logo.png" alt="Craidle Intelligence Group" className="h-10 sm:h-14 w-auto" />
            </div>
            <div className="flex space-x-4">
              <a href="YOUR_FACEBOOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </a>
              <a href="https://www.instagram.com/teaich_app/" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a href="YOUR_TIKTOK_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.27 8.27 0 004.84 1.56V6.81a4.85 4.85 0 01-1.07-.12z"/>
                </svg>
              </a>
              <a href="YOUR_LINKEDIN_URL_HERE" target="_blank" rel="noopener noreferrer" className="text-ink-900 hover:opacity-70 transition-opacity">
                <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
            <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
              <button onClick={() => navigate('/feedback')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Feedback</button>
              <button onClick={() => navigate('/legal/privacy-policy')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Privacy Policy</button>
              <button onClick={() => navigate('/legal/terms-of-service')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Terms of Service</button>
              <button onClick={() => navigate('/legal/data-retention')} className="text-ink-900 hover:underline text-xs sm:text-sm font-semibold">Data Retention</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PurchasePage;